// src/pages/calidad/BpmSuspendidos.jsx
// Personal suspendido (BPMs) — pestañas Activas / En riesgo / Historial.
// Suspensión = 12 puntos acumulados en el trimestre calendario.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiInfo } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';
import BpmFichaPersona from './BpmFichaPersona';

Chart.register(...registerables);

const BASE = '/api/calidad/bpm-analisis';
const PUNTOS_SUSPENSION = 12;
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', naranja: '#B2662A' };
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function Grafica({ config, alto = 240 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return <div style={{ position: 'relative', height: alto }}><canvas ref={ref} /></div>;
}

function BarraSaldo({ saldo }) {
  const v = Math.max(0, Math.min(PUNTOS_SUSPENSION, saldo || 0));
  const color = v >= 8 ? COLOR.rojo : v >= 4 ? COLOR.amarillo : COLOR.verde;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 120 }}>
      <div style={{ flex: 1, height: 8, background: '#F0E9E0', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${(v / PUNTOS_SUSPENSION) * 100}%`, background: color }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color }}>{saldo}/{PUNTOS_SUSPENSION}</span>
    </div>
  );
}

export default function BpmSuspendidos() {
  const [tab, setTab] = useState('activas');
  const [todas, setTodas] = useState([]);
  const [riesgo, setRiesgo] = useState({ data: [], umbral: 8, trimestre: null });
  const [umbral, setUmbral] = useState(8);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [marcandoId, setMarcandoId] = useState(null);
  const [fichaId, setFichaId] = useState(null);
  const [filtroHist, setFiltroHist] = useState({ estatus: '', desde: `${new Date().getFullYear()}-01-01`, hasta: aISO(new Date()), busqueda: '' });

  async function cargarSuspensiones() {
    const r = await api.get(`${BASE}/suspensiones`, { params: { estatus: 'todas' } });
    setTodas(r.data.data);
  }
  async function cargarRiesgo(u = umbral) {
    const r = await api.get(`${BASE}/en-riesgo`, { params: { desde_puntos: u } });
    setRiesgo(r.data);
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        await Promise.all([cargarSuspensiones(), cargarRiesgo()]);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el personal suspendido. Revisa que la ruta /api/calidad/bpm-analisis esté montada.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleMarcarCumplida(f) {
    if (!window.confirm(`¿Confirmas que ${f.empleado_nombre} ya cumplió su suspensión del ${fmtFecha(f.fecha_suspension)}?`)) return;
    try {
      setMarcandoId(f.id);
      setMensaje(null);
      await api.put(`/api/calidad/bpm-suspensiones/${f.id}/cumplir`);
      setMensaje({ tipo: 'ok', texto: `✓ ${f.empleado_nombre} — suspensión marcada como cumplida.` });
      await cargarSuspensiones();
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.status === 403 ? 'No tienes permiso de captura en Calidad.' : err.response?.data?.error || 'No se pudo marcar la suspensión.' });
    } finally {
      setMarcandoId(null);
    }
  }

  // ─── Derivados ───
  const activas = todas.filter((f) => f.estatus === 'activa');
  const trimestreIni = useMemo(() => { const h = new Date(); return aISO(new Date(h.getFullYear(), Math.floor(h.getMonth() / 3) * 3, 1)); }, []);
  const delTrimestre = todas.filter((f) => aISO(new Date(f.fecha_suspension)) >= trimestreIni);
  const anio = String(new Date().getFullYear());
  const delAnio = todas.filter((f) => aISO(new Date(f.fecha_suspension)).startsWith(anio));

  const porArea = useMemo(() => {
    const m = {};
    delAnio.forEach((f) => { const a = f.area || 'Sin área'; m[a] = (m[a] || 0) + 1; });
    return Object.entries(m).map(([area, n]) => ({ area, n })).sort((a, b) => b.n - a.n);
  }, [delAnio]);
  const porPersona = useMemo(() => {
    const m = {};
    delAnio.forEach((f) => {
      if (!m[f.empleado_id]) m[f.empleado_id] = { empleado_id: f.empleado_id, nombre: f.empleado_nombre, area: f.area, n: 0 };
      m[f.empleado_id].n++;
    });
    return Object.values(m).sort((a, b) => b.n - a.n);
  }, [delAnio]);

  const historialFiltrado = useMemo(() => todas.filter((f) => {
    const fecha = aISO(new Date(f.fecha_suspension));
    if (filtroHist.estatus && f.estatus !== filtroHist.estatus) return false;
    if (filtroHist.desde && fecha < filtroHist.desde) return false;
    if (filtroHist.hasta && fecha > filtroHist.hasta) return false;
    const q = filtroHist.busqueda.trim().toLowerCase();
    if (q && !(`${f.empleado_nombre} ${f.area || ''}`.toLowerCase().includes(q))) return false;
    return true;
  }), [todas, filtroHist]);

  const configMeses = useMemo(() => {
    const meses = Array.from({ length: 12 }, (_, i) => i);
    const conteo = (estatus) => meses.map((m) => delAnio.filter((f) => new Date(f.fecha_suspension).getMonth() === m && (!estatus || f.estatus === estatus)).length);
    return {
      type: 'bar',
      data: {
        labels: meses.map((m) => new Date(2026, m, 1).toLocaleDateString('es-MX', { month: 'short' })),
        datasets: [
          { label: 'Cumplidas', data: conteo('cumplida'), backgroundColor: COLOR.verde, stack: 's' },
          { label: 'Activas', data: conteo('activa'), backgroundColor: COLOR.rojo, stack: 's' },
        ],
      },
      options: { maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } }, plugins: { legend: { position: 'bottom' } } },
    };
  }, [delAnio]);

  const configAreas = useMemo(() => ({
    type: 'bar',
    data: { labels: porArea.slice(0, 10).map((a) => a.area), datasets: [{ label: 'Suspensiones', data: porArea.slice(0, 10).map((a) => a.n), backgroundColor: COLOR.morado }] },
    options: { indexAxis: 'y', maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } },
  }), [porArea]);

  function excel() {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(historialFiltrado.map((f) => ({
      Persona: f.empleado_nombre, Área: f.area || '', 'Fecha de suspensión': fmtFecha(f.fecha_suspension), Puntos: f.puntos_en_suspension,
      Estatus: f.estatus === 'activa' ? 'Activa' : 'Cumplida', 'Cumplida el': fmtFecha(f.fecha_cumplida),
      'Suspensiones en el año': f.suspensiones_anio, 'Saldo actual del trimestre': f.saldo_trimestre, 'Agregada a mano': f.manual ? 'Sí' : '',
    }))), 'Suspensiones');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(riesgo.data.map((p) => ({
      Persona: p.nombre, Área: p.area, 'Saldo del trimestre': p.saldo, 'Le faltan para suspensión': p.faltan, 'Última incidencia': fmtFecha(p.ultima_incidencia),
    }))), 'En riesgo');
    XLSX.writeFile(wb, `BPM_suspensiones_${aISO(new Date())}.xlsx`);
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Personal Suspendido (BPMs)</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>Una suspensión se genera al llegar a {PUNTOS_SUSPENSION} puntos en el trimestre</div>
        </div>
        <button onClick={excel} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Descargar Excel</button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.kgrid}>
        <Tarjeta titulo="Suspensiones activas" valor={activas.length} color={COLOR.rojo} sub="Pendientes de aplicar" onClick={() => setTab('activas')} />
        <Tarjeta titulo="En riesgo" valor={riesgo.data.length} color={COLOR.naranja} sub={`Con ${riesgo.umbral}+ puntos en el trimestre`} onClick={() => setTab('riesgo')} />
        <Tarjeta titulo="Suspensiones este trimestre" valor={delTrimestre.length} color={COLOR.morado} sub={`${delTrimestre.filter((f) => f.estatus === 'cumplida').length} cumplidas`} onClick={() => setTab('historial')} />
        <Tarjeta titulo={`Área con más suspensiones (${anio})`} valor={porArea[0]?.area || '—'} color={COLOR.gris} sub={porArea[0] ? `${porArea[0].n} suspensiones` : 'Sin suspensiones este año'} texto />
      </div>

      <div style={s.tabBar}>
        {[['activas', `Activas (${activas.length})`], ['riesgo', `En riesgo (${riesgo.data.length})`], ['historial', 'Historial']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {tab === 'activas' && (
        activas.length === 0 ? (
          <div style={{ ...s.tarjeta, textAlign: 'center', color: COLOR.gris }}>No hay suspensiones activas 🎉</div>
        ) : (
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Persona</th>
                  <th style={s.th}>Área</th>
                  <th style={s.th}>Suspendida desde</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Días</th>
                  <th style={s.th}>Saldo actual</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Suspensiones {anio}</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {activas.map((f) => (
                  <tr key={f.id}>
                    <td style={s.td}>
                      <button style={x.link} onClick={() => setFichaId(f.empleado_id)}>{f.empleado_nombre}</button>
                      {f.manual && <div style={x.sub}>agregada a mano</div>}
                    </td>
                    <td style={s.td}>{f.area || '—'}</td>
                    <td style={s.td}>{fmtFecha(f.fecha_suspension)}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: f.dias_desde > 7 ? COLOR.rojo : undefined, fontWeight: f.dias_desde > 7 ? 700 : 400 }}>{f.dias_desde}</td>
                    <td style={s.td}><BarraSaldo saldo={f.saldo_trimestre} /></td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: f.suspensiones_anio > 1 ? 700 : 400, color: f.suspensiones_anio > 1 ? COLOR.rojo : undefined }}>{f.suspensiones_anio}</td>
                    <td style={s.td}>
                      <button onClick={() => handleMarcarCumplida(f)} disabled={marcandoId === f.id} style={x.botonCumplida}>
                        {marcandoId === f.id ? '...' : 'Marcar cumplida'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'riesgo' && (
        <>
          <div style={{ ...s.filtrosBar, alignItems: 'center' }}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mostrar desde</span>
              <select style={s.filtroInput} value={umbral} onChange={(e) => { const u = Number(e.target.value); setUmbral(u); cargarRiesgo(u); }}>
                {[4, 6, 8, 10].map((u) => <option key={u} value={u}>{u} puntos</option>)}
              </select>
            </div>
            <div style={{ fontSize: 12, color: COLOR.gris }}>
              Personas cerca de una suspensión en el trimestre {riesgo.trimestre && `(${fmtFecha(riesgo.trimestre.inicio)} – ${fmtFecha(riesgo.trimestre.fin)})`}. Sirve para hablar con ellas antes de llegar a {PUNTOS_SUSPENSION}.
            </div>
          </div>
          {riesgo.data.length === 0 ? (
            <div style={{ ...s.tarjeta, textAlign: 'center', color: COLOR.gris }}>Nadie con {riesgo.umbral} puntos o más este trimestre.</div>
          ) : (
            <div style={s.tablaScroll}>
              <table style={s.tabla}>
                <thead>
                  <tr>
                    <th style={s.th}>Persona</th>
                    <th style={s.th}>Área</th>
                    <th style={s.th}>Saldo del trimestre</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Le faltan</th>
                    <th style={s.th}>Última incidencia</th>
                  </tr>
                </thead>
                <tbody>
                  {riesgo.data.map((p) => (
                    <tr key={p.empleado_id}>
                      <td style={s.td}><button style={x.link} onClick={() => setFichaId(p.empleado_id)}>{p.nombre}</button></td>
                      <td style={s.td}>{p.area}</td>
                      <td style={s.td}><BarraSaldo saldo={p.saldo} /></td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: p.faltan <= 3 ? COLOR.rojo : COLOR.naranja }}>{p.faltan} pts</td>
                      <td style={s.td}>{fmtFecha(p.ultima_incidencia)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'historial' && (
        <>
          <div style={s.grid2}>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Suspensiones por mes ({anio})</div>
              <Grafica config={configMeses} />
            </div>
            <div style={s.tarjeta}>
              <div style={s.seccionTitulo}>Por área ({anio})</div>
              {porArea.length ? <Grafica config={configAreas} alto={Math.max(180, Math.min(10, porArea.length) * 32 + 40)} /> : <div style={{ fontSize: 12, color: COLOR.gris }}>Sin suspensiones este año.</div>}
            </div>
          </div>

          {porPersona.some((p) => p.n > 1) && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 12 }}>
              Con más de una suspensión en {anio}: {porPersona.filter((p) => p.n > 1).map((p) => `${p.nombre} (${p.n})`).join(', ')}.
            </div>
          )}

          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Estatus</span>
              <select style={s.filtroInput} value={filtroHist.estatus} onChange={(e) => setFiltroHist({ ...filtroHist, estatus: e.target.value })}>
                <option value="">Todas</option>
                <option value="activa">Activas</option>
                <option value="cumplida">Cumplidas</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtroHist.desde} onChange={(e) => setFiltroHist({ ...filtroHist, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtroHist.hasta} onChange={(e) => setFiltroHist({ ...filtroHist, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input type="text" placeholder="Persona o área…" style={s.filtroInput} value={filtroHist.busqueda} onChange={(e) => setFiltroHist({ ...filtroHist, busqueda: e.target.value })} />
            </div>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Persona</th>
                  <th style={s.th}>Área</th>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>Estatus</th>
                  <th style={s.th}>Cumplida el</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>En el año</th>
                </tr>
              </thead>
              <tbody>
                {historialFiltrado.length === 0 && <tr><td colSpan={6} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>Sin suspensiones con estos filtros.</td></tr>}
                {historialFiltrado.map((f) => (
                  <tr key={f.id}>
                    <td style={s.td}><button style={x.link} onClick={() => setFichaId(f.empleado_id)}>{f.empleado_nombre}</button></td>
                    <td style={s.td}>{f.area || '—'}</td>
                    <td style={s.td}>{fmtFecha(f.fecha_suspension)}</td>
                    <td style={s.td}>
                      <span style={{ ...x.tag, color: f.estatus === 'activa' ? COLOR.rojo : COLOR.verde, background: f.estatus === 'activa' ? '#FDF3F1' : '#F0F7F1' }}>
                        {f.estatus === 'activa' ? 'Activa' : 'Cumplida'}
                      </span>
                    </td>
                    <td style={s.td}>{fmtFecha(f.fecha_cumplida)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{f.suspensiones_anio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ ...s.seccionKpiBloque, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
          <div style={s.seccionKpiFormula}>
            Cada incidencia suma los puntos de su nivel de riesgo. Al juntar {PUNTOS_SUSPENSION} puntos dentro del trimestre calendario se genera una suspensión;
            el sobrante se queda como saldo y el trimestre siguiente empieza en cero. Marca "Cumplida" cuando la suspensión ya se aplicó.
            Haz clic en un nombre para ver todo su historial.
          </div>
        </div>
      </div>

      <BpmFichaPersona empleadoId={fichaId} onCerrar={() => setFichaId(null)} />
    </div>
  );
}

const Tarjeta = ({ titulo, valor, color, sub, onClick, texto }) => (
  <div style={{ ...s.kcard, borderTop: `3px solid ${color}`, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <div style={s.kLbl}>{titulo}</div>
    <div style={{ ...s.kVal, fontSize: texto ? 18 : 30, color, marginTop: texto ? 6 : 0 }}>{valor}</div>
    <div style={s.kSub}>{sub}</div>
  </div>
);

const x = {
  tag: { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  sub: { fontSize: 10.5, color: '#8A7690' },
  link: { background: 'none', border: 'none', padding: 0, color: '#4A1E5C', fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' },
  botonCumplida: { padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#6E4F80', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' },
};